import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  Paper,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Stack,
  Card,
  CardContent,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  LocalPharmacy as LocalPharmacyIcon,
} from '@mui/icons-material';
import { addDays, format, parseISO } from 'date-fns';
import { MedicationToAdd, SoapTreatment } from '../../types/soap';
import DeaBottleSelect from '../DEALogs/DeaBottleSelect';
import { useClinicTimezone } from '../../hooks/useClinicTimezone';
import {
  formatInstantInZone,
  todayLocalISO,
  ymdToNoonUtcIso,
} from '../../utils/dateUtils';

interface MedicationConfirmationDialogProps {
  open: boolean;
  medications: SoapTreatment[];
  noteDate: Date | string;
  onConfirm: (selectedMedications: MedicationToAdd[]) => void;
  onCancel: () => void;
}

interface EditableMedication extends MedicationToAdd {
  selected: boolean;
  originalIndex: number;
}

/**
 * Parse duration string into number of days
 */
const parseDurationInDays = (duration?: string): number => {
  if (!duration) return 0;
  
  const durLower = duration.toLowerCase().trim();
  
  const dayMatch = durLower.match(/(\d+)\s*(?:day|d)/);
  const weekMatch = durLower.match(/(\d+)\s*(?:week|wk)/);
  const monthMatch = durLower.match(/(\d+)\s*(?:month|mo)/);
  
  if (dayMatch) {
    return parseInt(dayMatch[1]);
  } else if (weekMatch) {
    return parseInt(weekMatch[1]) * 7;
  } else if (monthMatch) {
    return parseInt(monthMatch[1]) * 30;
  }
  
  return 0;
};

/**
 * Calculate total quantity from frequency and duration
 */
const calculateQuantity = (frequency?: string, duration?: string): number => {
  if (!frequency || !duration) {
    return 1;
  }

  // Parse frequency (times per day)
  let timesPerDay = 1;
  const freqLower = frequency.toLowerCase().trim();
  
  // Check for specific frequency patterns
  if (freqLower.includes('bid') || freqLower.includes('twice')) {
    timesPerDay = 2;
  } else if (freqLower.includes('tid') || freqLower.includes('three times')) {
    timesPerDay = 3;
  } else if (freqLower.includes('qid') || freqLower.includes('four times')) {
    timesPerDay = 4;
  } else if (freqLower.includes('sid') || freqLower.includes('once') || freqLower.includes('daily') || freqLower.includes('qd')) {
    timesPerDay = 1;
  } else if (freqLower.includes('eod') || freqLower.includes('every other')) {
    timesPerDay = 0.5;
  } else if (freqLower.includes('q12')) {
    timesPerDay = 2;
  } else if (freqLower.includes('q8') || freqLower.includes('every 8 hours')) {
    timesPerDay = 3;
  } else if (freqLower.includes('q6') || freqLower.includes('every 6 hours')) {
    timesPerDay = 4;
  } else if (freqLower.includes('q4') || freqLower.includes('every 4 hours')) {
    timesPerDay = 6;
  } else {
    // Try to extract number from "3x daily" or "2 times daily"
    const match = freqLower.match(/(\d+)\s*(?:x|times)/);
    if (match) {
      timesPerDay = parseInt(match[1]);
    }
  }

  // Parse duration (days)
  const days = parseDurationInDays(duration);
  if (days === 0) {
    return 1;
  }

  const total = Math.ceil(timesPerDay * days);
  return total > 0 ? total : 1;
};

/**
 * Per-medication Rx controls — shared by the card (mobile/tablet) and table
 * (desktop) layouts. Flipping a medication to "Take-home Rx" makes the SOAP
 * completion create it as a real Rx record (refillable, dispensable, label-
 * printable) instead of a flat in-clinic record. Schedule II disables refills
 * (federal law — each fill needs a new Rx; the backend clamps it too).
 */
const RxControls: React.FC<{
  med: EditableMedication;
  index: number;
  onChange: (
    index: number,
    field: keyof EditableMedication,
    value: string | number | boolean,
  ) => void;
}> = ({ med, index, onChange }) => {
  const disabled = !med.selected;
  const isRx = !!med.isRx;
  const isC2 = med.deaSchedule === 'II';
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
        p: 1,
        borderRadius: 1,
        bgcolor: 'grey.50',
      }}
    >
      <ToggleButtonGroup
        value={isRx ? 'rx' : 'inclinic'}
        exclusive
        size="small"
        disabled={disabled}
        onChange={(_, val) => {
          if (val) onChange(index, 'isRx', val === 'rx');
        }}
      >
        <ToggleButton value="inclinic" sx={{ textTransform: 'none' }}>
          In-clinic
        </ToggleButton>
        <ToggleButton value="rx" sx={{ textTransform: 'none' }}>
          <LocalPharmacyIcon fontSize="small" sx={{ mr: 0.5 }} />
          Take-home Rx
        </ToggleButton>
      </ToggleButtonGroup>

      {isRx && (
        <>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id={`rx-refills-${index}`}>Refills authorized</InputLabel>
            <Select
              labelId={`rx-refills-${index}`}
              label="Refills authorized"
              value={med.refillsAuthorized ?? 0}
              disabled={disabled || isC2}
              onChange={(e) => onChange(index, 'refillsAuthorized', Number(e.target.value))}
            >
              <MenuItem value={0}>0 — initial fill only</MenuItem>
              <MenuItem value={1}>1 refill (2 fills total)</MenuItem>
              <MenuItem value={2}>2 refills (3 fills total)</MenuItem>
              <MenuItem value={3}>3 refills (4 fills total)</MenuItem>
              <MenuItem value={4}>4 refills (5 fills total)</MenuItem>
              <MenuItem value={5}>5 refills (6 fills total)</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id={`rx-dea-${index}`}>DEA Schedule</InputLabel>
            <Select
              labelId={`rx-dea-${index}`}
              label="DEA Schedule"
              value={med.deaSchedule ?? ''}
              disabled={disabled}
              onChange={(e) => {
                const val = String(e.target.value);
                onChange(index, 'deaSchedule', val);
                // Schedule II can't be refilled — force refills to 0.
                if (val === 'II') onChange(index, 'refillsAuthorized', 0);
              }}
            >
              <MenuItem value="">Not controlled</MenuItem>
              <MenuItem value="II">Schedule II</MenuItem>
              <MenuItem value="III">Schedule III</MenuItem>
              <MenuItem value="IV">Schedule IV</MenuItem>
              <MenuItem value="V">Schedule V</MenuItem>
            </Select>
          </FormControl>

          {isC2 && (
            <Typography variant="caption" color="text.secondary">
              Schedule II — refills not permitted; each fill needs a new Rx
            </Typography>
          )}
        </>
      )}

      {/* DEA bottle prompt — applies to in-clinic charge-outs AND take-home Rx
        * (both dispense physically); renders only when the drug single-matches
        * the clinic's CS registry, preselecting the FIFO bottle (ADR-0014). */}
      <Box sx={{ flexBasis: '100%', minWidth: 240 }}>
        <DeaBottleSelect
          drugName={med.name}
          declaredSchedule={med.deaSchedule || undefined}
          value={med.deaLotId ?? ''}
          onChange={(lotId) => onChange(index, 'deaLotId', lotId)}
          quantity={med.quantity || undefined}
          disabled={disabled}
          margin="none"
        />
      </Box>
    </Box>
  );
};

const MedicationConfirmationDialog: React.FC<MedicationConfirmationDialogProps> = ({
  open,
  medications,
  noteDate,
  onConfirm,
  onCancel,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [editableMedications, setEditableMedications] = useState<EditableMedication[]>([]);

  /*
   * THE MEDICATION DATES THIS DIALOG POSTS ARE CALENDAR DAYS.
   *
   * `noteDate` is an INSTANT (`SoapNote.date` — DATE-KINDS.md §1.2), but a
   * medication's start and end are DAYS: nobody means "this course starts at
   * 4:07 PM". Posting the raw instant is what made `Medication.startDate` a
   * MIXED column — the same column the medication dialog and barn ops fill with
   * picked days — and a MIXED column has no correct render. So the day is
   * derived here, once, and posted on the shared anchor.
   *
   * WHICH day: the CLINIC's, not the browser's. A note completed at 9 PM
   * Eastern by a relief vet sitting in California belongs to the clinic's
   * evening, not to California's afternoon — and `Clinic.timezone` is on the
   * wire already (`useClinicTimezone`). `formatInstantInZone(..., 'yyyy-MM-dd')`
   * is the sanctioned way to ask an instant which day it is in a named zone.
   *
   * WHERE it is parked: noon UTC (`ymdToNoonUtcIso`), the one anchor in this
   * codebase, which the backend then passes through untouched.
   */
  const clinicTz = useClinicTimezone();
  const noteDayYmd = useMemo(
    () => formatInstantInZone(noteDate, clinicTz, 'yyyy-MM-dd') || todayLocalISO(),
    [noteDate, clinicTz],
  );
  /** The note's day, on the anchor — the start date every row here posts. */
  const noteDayIso = useMemo(() => ymdToNoonUtcIso(noteDayYmd), [noteDayYmd]);
  /** `noteDay + n` as another anchored day (a DAY count, never `n * 86400000`
   *  added to an instant, which carries the note's time of day along with it). */
  const dayAfterNoteDay = useCallback(
    (days: number) => ymdToNoonUtcIso(format(addDays(parseISO(noteDayYmd), days), 'yyyy-MM-dd')),
    [noteDayYmd],
  );

  // Initialize editable medications when dialog opens
  useEffect(() => {
    if (open && medications.length > 0) {
      const initialized = medications.map((med, index) => {
        const quantity = calculateQuantity(med.frequency, med.duration);
        const days = parseDurationInDays(med.duration);
        const instructions = [med.dosage, med.frequency, med.duration, med.route]
          .filter(Boolean)
          .join(', ');

        const startDate = noteDayIso;
        const endDate = days > 0 ? dayAfterNoteDay(days) : undefined;

        return {
          selected: true, // All selected by default
          originalIndex: index,
          name: med.name,
          dosage: med.dosage || '',
          frequency: med.frequency || '',
          duration: med.duration || '',
          route: med.route || '',
          quantity,
          instructions,
          startDate,
          endDate,
          // Rx fields default to off — most SOAP-plan meds are in-clinic
          // administrations; the vet flips the take-home ones.
          isRx: false,
          refillsAuthorized: 0,
          deaSchedule: '',
        };
      });
      setEditableMedications(initialized);
    }
  }, [open, medications, noteDayIso, dayAfterNoteDay]);

  const handleToggleSelection = (index: number) => {
    setEditableMedications(prev =>
      prev.map((med, i) =>
        i === index ? { ...med, selected: !med.selected } : med
      )
    );
  };

  const handleAddMedication = () => {
    const newMedication: EditableMedication = {
      selected: true,
      originalIndex: editableMedications.length,
      name: '',
      dosage: '',
      frequency: '',
      duration: '',
      route: '',
      quantity: 1,
      instructions: '',
      startDate: noteDayIso,
      endDate: undefined,
      isRx: false,
      refillsAuthorized: 0,
      deaSchedule: '',
    };
    setEditableMedications(prev => [...prev, newMedication]);
  };

  const handleDeleteMedication = (index: number) => {
    setEditableMedications(prev => prev.filter((_, i) => i !== index));
  };

  const handleFieldChange = (
    index: number,
    field: keyof EditableMedication,
    value: string | number | boolean,
  ) => {
    setEditableMedications(prev =>
      prev.map((med, i) => {
        if (i !== index) return med;

        const updated = { ...med, [field]: value };

        // Recalculate quantity if frequency or duration changes
        if (field === 'frequency' || field === 'duration') {
          updated.quantity = calculateQuantity(updated.frequency, updated.duration);
          
          // Recalculate instructions
          updated.instructions = [updated.dosage, updated.frequency, updated.duration, updated.route]
            .filter(Boolean)
            .join(', ');

          // Recalculate end date
          const days = parseDurationInDays(updated.duration);
          updated.endDate = days > 0 ? dayAfterNoteDay(days) : undefined;
        }

        // Update instructions if dosage or route changes
        if (field === 'dosage' || field === 'route') {
          updated.instructions = [updated.dosage, updated.frequency, updated.duration, updated.route]
            .filter(Boolean)
            .join(', ');
        }

        return updated;
      })
    );
  };

  const handleConfirm = () => {
    const selectedMeds = editableMedications
      .filter(med => med.selected)
      // '' deaLotId (cleared picker) → omit, like the other dispense dialogs.
      .map(({ selected, originalIndex, ...med }) => ({ ...med, deaLotId: med.deaLotId || undefined }));
    onConfirm(selectedMeds);
  };

  const selectedCount = editableMedications.filter(med => med.selected).length;

  return (
    <Dialog 
      open={open} 
      onClose={onCancel}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { 
          minHeight: { xs: '100%', sm: '70vh' }, 
          maxHeight: { xs: '100%', sm: '90vh' },
          m: { xs: 0, sm: 2 }
        }
      }}
    >
      <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexDirection={{ xs: 'column', sm: 'row' }} gap={1}>
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Confirm Medications to Add
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            {selectedCount} of {editableMedications.length} selected
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2 } }}>
        <Box mb={2}>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            Review and edit medications before adding them to the medical record. 
            Uncheck medications you don't want to add, or adjust quantities and details as needed.
          </Typography>
          <Stack direction="row" spacing={1} mt={1}>
            <Button
              size={isMobile ? "small" : "medium"}
              startIcon={!isMobile && <AddIcon />}
              onClick={handleAddMedication}
              variant="contained"
              color="primary"
              fullWidth={isMobile}
              sx={{ minHeight: 44, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
            >
              {isMobile ? 'Add' : 'Add Medication'}
            </Button>
          </Stack>
        </Box>

        {isMobile || isTablet ? (
          // Card layout for mobile/tablet
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {editableMedications.map((med, index) => (
              <Card 
                key={index} 
                variant="outlined"
                sx={{ 
                  bgcolor: med.selected ? 'transparent' : 'action.disabledBackground',
                  position: 'relative'
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Checkbox
                        checked={med.selected}
                        onChange={() => handleToggleSelection(index)}
                        size="small"
                      />
                      <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '0.85rem' }}>
                        Medication {index + 1}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteMedication(index)}
                      color="error"
                      sx={{ mt: -0.5, mr: -0.5 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Stack spacing={1.5}>
                    <TextField
                      label="Name"
                      value={med.name}
                      onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
                      size="small"
                      fullWidth
                      disabled={!med.selected}
                    />
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                      <TextField
                        label="Dosage"
                        value={med.dosage}
                        onChange={(e) => handleFieldChange(index, 'dosage', e.target.value)}
                        size="small"
                        fullWidth
                        disabled={!med.selected}
                      />
                      <TextField
                        label="Route"
                        value={med.route}
                        onChange={(e) => handleFieldChange(index, 'route', e.target.value)}
                        size="small"
                        fullWidth
                        disabled={!med.selected}
                      />
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                      <TextField
                        label="Frequency"
                        value={med.frequency}
                        onChange={(e) => handleFieldChange(index, 'frequency', e.target.value)}
                        size="small"
                        fullWidth
                        disabled={!med.selected}
                      />
                      <TextField
                        label="Duration"
                        value={med.duration}
                        onChange={(e) => handleFieldChange(index, 'duration', e.target.value)}
                        size="small"
                        fullWidth
                        disabled={!med.selected}
                      />
                    </Box>
                    <TextField
                      label="Quantity"
                      type="number"
                      value={med.quantity}
                      onChange={(e) => handleFieldChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                      size="small"
                      fullWidth
                      disabled={!med.selected}
                      inputProps={{ min: 0, step: 0.5 }}
                    />
                    <TextField
                      label="Instructions"
                      value={med.instructions}
                      onChange={(e) => handleFieldChange(index, 'instructions', e.target.value)}
                      size="small"
                      fullWidth
                      disabled={!med.selected}
                      multiline
                      rows={2}
                    />
                    <RxControls med={med} index={index} onChange={handleFieldChange} />
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          // Table layout for desktop
          <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell padding="checkbox" width={50}>
                  Select
                </TableCell>
                <TableCell width={150}>Name</TableCell>
                <TableCell width={120}>Dosage</TableCell>
                <TableCell width={110}>Frequency</TableCell>
                <TableCell width={130}>Duration</TableCell>
                <TableCell width={80}>Route</TableCell>
                <TableCell width={40}>Quantity</TableCell>
                <TableCell width={210}>Instructions</TableCell>
                <TableCell width={20}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {editableMedications.map((med, index) => (
                <React.Fragment key={index}>
                <TableRow
                  sx={{
                    bgcolor: med.selected ? 'transparent' : 'action.disabledBackground',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={med.selected}
                      onChange={() => handleToggleSelection(index)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={med.name}
                      onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
                      size="small"
                      fullWidth
                      disabled={!med.selected}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={med.dosage}
                      onChange={(e) => handleFieldChange(index, 'dosage', e.target.value)}
                      size="small"
                      fullWidth
                      disabled={!med.selected}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={med.frequency}
                      onChange={(e) => handleFieldChange(index, 'frequency', e.target.value)}
                      size="small"
                      fullWidth
                      disabled={!med.selected}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={med.duration}
                      onChange={(e) => handleFieldChange(index, 'duration', e.target.value)}
                      size="small"
                      fullWidth
                      disabled={!med.selected}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={med.route}
                      onChange={(e) => handleFieldChange(index, 'route', e.target.value)}
                      size="small"
                      fullWidth
                      disabled={!med.selected}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      value={med.quantity}
                      onChange={(e) => handleFieldChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                      size="small"
                      fullWidth
                      disabled={!med.selected}
                      inputProps={{ min: 0, step: 0.5 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={med.instructions}
                      onChange={(e) => handleFieldChange(index, 'instructions', e.target.value)}
                      size="small"
                      fullWidth
                      disabled={!med.selected}
                      multiline
                      maxRows={2}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Delete medication">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteMedication(index)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                {/* Rx controls sub-row — spans the full table width. pt:1.5
                 * gives the FormControl floating labels room to clear the
                 * medication row above them. */}
                <TableRow
                  sx={{ bgcolor: med.selected ? 'transparent' : 'action.disabledBackground' }}
                >
                  <TableCell colSpan={9} sx={{ pt: 1.5, pb: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                    <RxControls med={med} index={index} onChange={handleFieldChange} />
                  </TableCell>
                </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        )}

        {selectedCount === 0 && (
          <Box mt={2}>
            <Typography variant="body2" color="warning.main" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              No medications selected. Click "Confirm & Complete" to complete the SOAP note without adding medications.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 }, pt: 1, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
        <Button 
          onClick={onCancel} 
          startIcon={!isMobile && <CancelIcon />}
          fullWidth={isMobile}
          sx={{ minHeight: 44, order: { xs: 2, sm: 1 } }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          startIcon={!isMobile && <CheckCircleIcon />}
          fullWidth={isMobile}
          sx={{ minHeight: 44, order: { xs: 1, sm: 2 } }}
        >
          {isMobile ? 'Confirm' : 'Confirm & Complete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MedicationConfirmationDialog;

